import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { useAuth, useNotifLog, pushNotif } from "../App";
import { api } from "../api";
import { EVENT_TYPES, DAYS_PT, MONTHS_PT, typeInfo, typeColors, getDaysInMonth, fmtDate, mapsUrl } from "../theme";
import { I } from "../Icons";
import { Toggle, Spinner, InfoRow, MRow, SideNavItem, Btn } from "../ui";
import EventForm from "./EventForm";

function useViewport() {
  const [vw, setVw] = useState(window.innerWidth);
  useEffect(() => {
    const fn = () => setVw(window.innerWidth);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return { vw, isMobile: vw < 600, isDesktop: vw >= 960 };
}

// Eventos do iCal podem ter um título livre, sem o sufixo /UF.
function eventTitle(ev) { return ev.state && ev.state !== "--" ? `${ev.city}/${ev.state}` : ev.city; }

export default function AgendaPage() {
  const { slug } = useParams();
  // notifOn e toggleNotif vem do contexto global (App.js) — nao declarar localmente
  const { token, user, agenda, logout, T, dark, toggleDark, toast, notifOn, toggleNotif } = useAuth();
  const { vw, isMobile, isDesktop } = useViewport();
  const notifLog = useNotifLog();

  const [events,     setEvents]    = useState([]);
  const [loading,    setLoading]   = useState(true);
  const [page,       setPage]      = useState("calendar");
  const [menuOpen,   setMenu]      = useState(false);
  const [notifPanel, setNP]        = useState(false);
  const [lastSeen,   setLastSeen]  = useState(0);
  const [detailEv,   setDetail]    = useState(null);
  const [copyModal,  setCopy]      = useState(false);
  const [legendOpen, setLegend]    = useState(false);

  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year,  setYear]  = useState(today.getFullYear());

  const unread = notifLog.filter(n => n.id > lastSeen).length;
  const isAdmin = user?.role === "admin" || user?.role === "master";

  const load = useCallback(async () => {
    setLoading(true);
    try { const d = await api.getAgenda(slug); setEvents(d.events); }
    catch { toast("Erro ao carregar agenda"); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  async function handleAdd(ev) {
    const saved = await api.addEvent(slug, ev, token);
    setEvents(p => [...p, saved]);
    toast("Show adicionado");
    setPage("calendar");
  }
  async function handleEdit(ev) {
    const saved = await api.editEvent(slug, ev.id, ev, token);
    setEvents(p => p.map(e => e.id === saved.id ? saved : e));
    toast("Alteracao salva");
    setPage("calendar");
    if (detailEv?.id === saved.id) setDetail(saved);
  }
  async function handleDelete(id) {
    await api.deleteEvent(slug, id, token);
    setEvents(p => p.filter(e => e.id !== id));
    toast("Show removido");
    setDetail(null);
  }

  function prevMonth() { if (month === 0) { setMonth(11); setYear(y => y - 1); } else setMonth(m => m - 1); }
  function nextMonth() { if (month === 11) { setMonth(0); setYear(y => y + 1); } else setMonth(m => m + 1); }

  const px = isMobile ? 12 : isDesktop ? 28 : 20;
  const logoUrl = agenda?.logoUrl
    ? (agenda.logoUrl.startsWith("http") ? agenda.logoUrl : (process.env.REACT_APP_API_URL || "") + agenda.logoUrl)
    : null;

  if (loading) return <div style={{ background: T.bg, minHeight: "100vh" }}><Spinner T={T} /></div>;

  function ibtn(T) { return { background: "none", border: "none", cursor: "pointer", color: T.headerText, padding: 7, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }; }

  return (
    <div style={{ fontFamily: "'Inter','Helvetica Neue',Arial,sans-serif", background: T.bg, minHeight: "100vh", color: T.text }}>

      {/* HEADER */}
      <header style={{ background: T.header, height: 52, display: "flex", alignItems: "center", justifyContent: "space-between", padding: `0 ${isMobile ? 12 : 20}px`, position: "sticky", top: 0, zIndex: 50, boxShadow: `0 2px 10px ${T.shadow}` }}>
        <button onClick={() => setMenu(o => !o)} style={ibtn(T)}><I n="Menu" size={20} /></button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {logoUrl && <img src={logoUrl} alt="" style={{ height: 28, width: 28, borderRadius: 6, objectFit: "cover" }} />}
          <span style={{ color: T.headerText, fontWeight: 800, fontSize: isMobile ? 12 : 14, letterSpacing: 1.5, textTransform: "uppercase" }}>
            {agenda?.name || "AGENDA DE SHOWS"}
          </span>
        </div>
        <div style={{ display: "flex", gap: 2 }}>
          <button onClick={() => { setNP(o => !o); setLastSeen(notifLog[0]?.id || 0); }} style={{ ...ibtn(T), position: "relative" }}>
            <I n="Bell" size={17} fill={notifOn ? "currentColor" : undefined} />
            {unread > 0 && <span style={{ position: "absolute", top: 4, right: 4, width: 8, height: 8, borderRadius: "50%", background: T.notifDot }} />}
          </button>
          <button onClick={toggleDark} style={ibtn(T)}><I n={dark ? "Sun" : "Moon"} size={17} /></button>
        </div>
      </header>

      {/* LAYOUT */}
      <div style={{ display: "flex", maxWidth: isDesktop ? 1100 : "100%", margin: "0 auto" }}>

        {/* DESKTOP SIDEBAR */}
        {isDesktop && (
          <aside style={{ width: 240, flexShrink: 0, background: T.surface, borderRight: `1px solid ${T.border}`, minHeight: "calc(100vh - 52px)", position: "sticky", top: 52, height: "calc(100vh - 52px)", overflowY: "auto", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "16px 0 8px" }}>
              <SideNavItem icon="Cal"  label="Calendario"       T={T} active={page === "calendar"} onClick={() => setPage("calendar")} />
              {isAdmin && <SideNavItem icon="Plus" label="Gerenciar shows" T={T} active={page === "addEvent"} onClick={() => setPage("addEvent")} />}
            </div>
            <div style={{ borderTop: `1px solid ${T.border}`, padding: "8px 0" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: T.text }}>
                  <I n="Bell" size={15} style={{ color: T.textSub }} /> Notificacoes
                </div>
                <Toggle on={notifOn} onChange={toggleNotif} T={T} />
              </div>
              <SideNavItem icon={dark ? "Sun" : "Moon"} label={dark ? "Tema claro" : "Tema escuro"} T={T} onClick={toggleDark} />
            </div>
            <div style={{ marginTop: "auto", borderTop: `1px solid ${T.border}`, padding: "12px 18px" }}>
              <button onClick={logout} style={{ display: "flex", alignItems: "center", gap: 8, background: "none", border: "none", cursor: "pointer", color: T.danger, fontSize: 13, fontWeight: 600, padding: "6px 0" }}>
                <I n="Logout" size={15} style={{ color: T.danger }} /> Sair
              </button>
            </div>
          </aside>
        )}

        <main style={{ flex: 1, minWidth: 0 }}>
          {page === "addEvent" && isAdmin
            ? <EventForm T={T} dark={dark} events={events} slug={slug} onAdd={handleAdd} onEdit={handleEdit} onDelete={handleDelete} onBack={() => setPage("calendar")} isMobile={isMobile} />
            : <CalendarView T={T} dark={dark} events={events} month={month} year={year} onPrev={prevMonth} onNext={nextMonth} isMobile={isMobile} isDesktop={isDesktop} px={px} onShowDetail={setDetail} legendOpen={legendOpen} setLegend={setLegend} copyModal={copyModal} setCopy={setCopy} />
          }
        </main>
      </div>

      {/* MOBILE MENU */}
      {!isDesktop && menuOpen && (
        <div style={{ position: "fixed", inset: 0, background: T.modalBg, zIndex: 100 }} onClick={() => setMenu(false)}>
          <div style={{ position: "absolute", top: 0, left: 0, width: 264, height: "100%", background: T.surface, boxShadow: `4px 0 24px ${T.shadowMd}`, display: "flex", flexDirection: "column" }} onClick={e => e.stopPropagation()}>
            <div style={{ background: T.header, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ color: T.headerText, fontWeight: 800, fontSize: 11, letterSpacing: 2 }}>MENU</span>
              <button onClick={() => setMenu(false)} style={ibtn(T)}><I n="X" size={17} /></button>
            </div>
            <div style={{ flex: 1, overflowY: "auto", padding: "8px 0" }}>
              <MRow icon="Cal"  label="Calendario"      T={T} onClick={() => { setPage("calendar"); setMenu(false); }} />
              {isAdmin && <MRow icon="Plus" label="Gerenciar shows" T={T} onClick={() => { setPage("addEvent"); setMenu(false); }} />}
              <div style={{ height: 1, background: T.border, margin: "8px 0" }} />
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 18px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, color: T.text, fontSize: 14 }}>
                  <I n="Bell" size={15} style={{ color: T.textSub }} /> Notificacoes
                </div>
                <Toggle on={notifOn} onChange={toggleNotif} T={T} />
              </div>
              <div style={{ height: 1, background: T.border, margin: "8px 0" }} />
              <MRow icon={dark ? "Sun" : "Moon"} label={dark ? "Tema claro" : "Tema escuro"} T={T} onClick={toggleDark} />
              <div style={{ height: 1, background: T.border, margin: "8px 0" }} />
              <MRow icon="Logout" label="Sair" T={T} color={T.danger} onClick={() => { logout(); setMenu(false); }} />
            </div>
            <div style={{ padding: "12px 18px", borderTop: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 11, color: T.textMuted }}>{user?.username} · {user?.role}</span>
            </div>
          </div>
        </div>
      )}

      {/* NOTIF PANEL */}
      {notifPanel && (
        <div style={{ position: "fixed", top: 52, right: 0, width: Math.min(310, vw), background: T.surface, borderLeft: `1px solid ${T.border}`, borderBottom: `1px solid ${T.border}`, zIndex: 60, maxHeight: 340, overflowY: "auto", boxShadow: `-4px 4px 24px ${T.shadow}` }}>
          <div style={{ padding: "12px 16px", borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: T.text }}>Notificacoes</span>
            <button onClick={() => setNP(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.textSub, display: "flex", padding: 4 }}><I n="X" size={15} /></button>
          </div>
          {notifLog.length === 0
            ? <p style={{ padding: 20, color: T.textMuted, fontSize: 13, textAlign: "center" }}>Nenhuma ainda.</p>
            : notifLog.map(n => (
              <div key={n.id} style={{ padding: "10px 16px", borderBottom: `1px solid ${T.border}`, background: n.id > lastSeen ? T.notifNew : T.surface }}>
                <div style={{ fontSize: 12, color: T.text }}>{n.msg}</div>
                <div style={{ fontSize: 10, color: T.textMuted, marginTop: 1 }}>{n.time}</div>
              </div>
            ))
          }
        </div>
      )}

      {/* EVENT DETAIL */}
      {detailEv && <EventDetail ev={detailEv} T={T} dark={dark} onClose={() => setDetail(null)} isMobile={isMobile} />}
    </div>
  );
}

// ── Calendar View ─────────────────────────────────────────────────────────────
function CalendarView({ T, dark, events, month, year, onPrev, onNext, isMobile, isDesktop, px, onShowDetail, legendOpen, setLegend, copyModal, setCopy }) {
  const daysInMonth = getDaysInMonth(year, month);
  const byDay = {};
  events.forEach(e => {
    const d = new Date(e.date + "T12:00:00");
    if (d.getFullYear() === year && d.getMonth() === month) {
      const day = d.getDate();
      if (!byDay[day]) byDay[day] = [];
      byDay[day].push(e);
    }
  });
  const todayObj = new Date();
  const isThisMonth = todayObj.getMonth() === month && todayObj.getFullYear() === year;

  return (
    <div style={{ paddingBottom: 60 }}>
      <div style={{ background: T.surface, padding: `12px ${px}px 10px`, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: 2, textTransform: "uppercase" }}>Agenda</div>
          <div style={{ fontSize: isMobile ? 17 : 20, fontWeight: 900, color: T.text }}>{MONTHS_PT[month]} {year}</div>
        </div>
        <button onClick={() => setLegend(o => !o)} style={{ background: legendOpen ? T.accent : T.surfaceAlt, border: `1px solid ${legendOpen ? T.accent : T.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: legendOpen ? T.accentText : T.textSub, display: "flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 600 }}>
          <I n="Filter" size={13} /> Legenda
        </button>
      </div>

      {legendOpen && (
        <div style={{ background: T.surfaceAlt, borderBottom: `1px solid ${T.border}`, padding: `12px ${px}px` }}>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${isMobile ? 2 : isDesktop ? 4 : 3}, 1fr)`, gap: "7px 10px" }}>
            {EVENT_TYPES.map(t => (
              <div key={t.key} style={{ display: "flex", alignItems: "center", gap: 6, padding: "5px 8px", background: T.surface, borderRadius: 7, border: `1px solid ${T.border}` }}>
                <span style={{ width: 10, height: 10, borderRadius: 3, background: t[dark ? "dark" : "light"], flexShrink: 0 }} />
                <span style={{ fontSize: 11, fontWeight: 600, color: T.textSub }}>{t.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ background: T.surface, display: "flex", alignItems: "center", justifyContent: "space-between", padding: `8px ${px}px`, borderBottom: `1px solid ${T.border}` }}>
        <button onClick={onPrev} style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: T.text, display: "flex" }}><I n="Left" size={16} /></button>
        <span style={{ fontSize: 14, fontWeight: 800, color: T.text, letterSpacing: 0.5 }}>{MONTHS_PT[month]} {year}</span>
        <button onClick={onNext} style={{ background: T.surfaceAlt, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", color: T.text, display: "flex" }}><I n="Right" size={16} /></button>
      </div>

      {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
        const dt = new Date(year, month, day);
        const dow = DAYS_PT[dt.getDay()];
        const isWE = dt.getDay() === 0 || dt.getDay() === 6;
        const isToday = isThisMonth && todayObj.getDate() === day;
        const dayEvs = byDay[day] || [];
        return (
          <div key={day} style={{ borderBottom: `1px solid ${T.sep}`, background: isToday ? T.todayBg : "transparent" }}>
            <div style={{ display: "flex", alignItems: "center", padding: `9px ${px}px 5px`, gap: 7 }}>
              <span style={{ fontSize: isMobile ? 22 : 26, fontWeight: 900, lineHeight: 1, color: isToday ? T.accent : isWE ? T.weekendColor : T.text, minWidth: isMobile ? 30 : 36 }}>{String(day).padStart(2, "0")}</span>
              <span style={{ fontSize: 10, fontWeight: 700, color: T.textMuted, letterSpacing: 1 }}>{dow}</span>
              {isToday && <span style={{ marginLeft: "auto", fontSize: 9, background: T.accent, color: T.accentText, borderRadius: 4, padding: "2px 7px", fontWeight: 700 }}>HOJE</span>}
            </div>
            {dayEvs.map(ev => {
              const { bg, text } = typeColors(ev.type, dark);
              return (
                <button key={ev.id} onClick={() => onShowDetail(ev)}
                  style={{ display: "block", width: `calc(100% - ${px * 2}px)`, margin: `0 ${px}px 6px`, borderRadius: 9, padding: "9px 12px", background: bg, border: "none", cursor: "pointer", textAlign: "left" }}
                  onMouseEnter={e => e.currentTarget.style.filter = "brightness(1.08)"}
                  onMouseLeave={e => e.currentTarget.style.filter = "none"}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 800, color: text }}>{eventTitle(ev)}</div>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 3, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, color: text, opacity: 0.85, display: "flex", alignItems: "center", gap: 3 }}><I n="Pin" size={11} />{ev.venue}</span>
                        {ev.time && <span style={{ fontSize: 11, color: text, opacity: 0.8, display: "flex", alignItems: "center", gap: 3 }}><I n="Clock" size={11} />{ev.time}</span>}
                        {ev.soundcheck && <span style={{ fontSize: 11, color: text, opacity: 0.8, display: "flex", alignItems: "center", gap: 3 }}><I n="Mic" size={11} />{ev.soundcheck}</span>}
                      </div>
                    </div>
                    <span style={{ fontSize: 9, background: "rgba(0,0,0,0.18)", color: text, padding: "2px 7px", borderRadius: 5, fontWeight: 700, flexShrink: 0, marginLeft: 6 }}>{typeInfo(ev.type).label}</span>
                  </div>
                </button>
              );
            })}
          </div>
        );
      })}

      <div style={{ padding: `20px ${px}px 4px` }}>
        <button onClick={() => setCopy(true)} style={{ width: "100%", background: T.surface, border: `1.5px solid ${T.border}`, borderRadius: 11, padding: 13, fontSize: 13, fontWeight: 700, cursor: "pointer", color: T.text, display: "flex", alignItems: "center", justifyContent: "center", gap: 9, boxShadow: `0 1px 4px ${T.shadow}` }}>
          <I n="Copy" size={16} /> COPIAR AGENDA
        </button>
      </div>

      {copyModal && <CopyModal T={T} dark={dark} events={events} month={month} year={year} onClose={() => setCopy(false)} isMobile={isMobile} />}
    </div>
  );
}

// ── Event Detail Sheet ────────────────────────────────────────────────────────
function EventDetail({ ev, T, dark, onClose, isMobile }) {
  const { bg, text } = typeColors(ev.type, dark);
  const t = typeInfo(ev.type);
  const dt = new Date(ev.date + "T12:00:00");
  const dow = DAYS_PT[dt.getDay()];
  const [tab, setTab] = useState("details");
  return (
    <div style={{ position: "fixed", inset: 0, background: T.modalBg, zIndex: 150, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: isMobile ? "20px 20px 0 0" : "16px", width: "100%", maxWidth: 460, maxHeight: "85vh", overflowY: "auto", animation: isMobile ? "slideUp 0.22s" : "fadeIn 0.18s", boxShadow: `0 -4px 40px ${T.shadowMd}` }} onClick={e => e.stopPropagation()}>
        <div style={{ background: bg, padding: "18px 18px 14px", borderRadius: isMobile ? "20px 20px 0 0" : "16px 16px 0 0", position: "relative" }}>
          <button onClick={onClose} style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.2)", border: "none", borderRadius: 8, cursor: "pointer", color: text, padding: 6, display: "flex" }}><I n="X" size={15} /></button>
          <div style={{ fontSize: 10, fontWeight: 700, color: text, opacity: 0.7, letterSpacing: 1, textTransform: "uppercase" }}>{t.label}</div>
          <div style={{ fontSize: 21, fontWeight: 900, color: text, lineHeight: 1.1, marginTop: 4 }}>{eventTitle(ev)}</div>
          <div style={{ fontSize: 13, color: text, opacity: 0.8, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
            <I n="Cal" size={12} /> {String(dt.getDate()).padStart(2, "0")}/{String(dt.getMonth() + 1).padStart(2, "0")}/{dt.getFullYear()} · {dow}
          </div>
        </div>
        <div style={{ padding: "14px 18px 24px" }}>
          {ev.notes && <div style={{ display:"flex", gap:8, borderBottom:`1px solid ${T.border}`, marginBottom:12 }}>
            <button onClick={()=>setTab("details")} style={{ background:"none", border:"none", borderBottom:`2px solid ${tab==="details"?T.accent:"transparent"}`, padding:"8px 4px", color:tab==="details"?T.accent:T.textSub, cursor:"pointer", fontSize:12, fontWeight:700 }}>DETALHES</button>
            <button onClick={()=>setTab("description")} style={{ background:"none", border:"none", borderBottom:`2px solid ${tab==="description"?T.accent:"transparent"}`, padding:"8px 4px", color:tab==="description"?T.accent:T.textSub, cursor:"pointer", fontSize:12, fontWeight:700 }}>DESCRIÇÃO</button>
          </div>}
          {tab === "description" && ev.notes ? (
            <div style={{ whiteSpace:"pre-wrap", fontSize:14, lineHeight:1.55, color:T.text, background:T.surfaceAlt, border:`1px solid ${T.border}`, borderRadius:9, padding:"12px 13px" }}>{ev.notes}</div>
          ) : <>
            <InfoRow icon="Pin"   label="Local"           value={ev.venue || "—"}      T={T} />
            {ev.time       && <InfoRow icon="Clock" label="Horario do show"  value={ev.time}       T={T} />}
            {ev.soundcheck && <InfoRow icon="Mic"   label="Passagem de som"  value={ev.soundcheck} T={T} accent />}
            <a href={mapsUrl(ev)} target="_blank" rel="noopener noreferrer"
              style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginTop: 18, background: T.mapBtn, color: T.mapBtnText, borderRadius: 10, padding: "12px 16px", textDecoration: "none", fontWeight: 700, fontSize: 14 }}>
              <I n="Nav" size={17} /> COMO CHEGAR
            </a>
          </>}
        </div>
      </div>
    </div>
  );
}

// ── Copy Modal ────────────────────────────────────────────────────────────────
function CopyModal({ T, dark, events, month, year, onClose, isMobile }) {
  const [sel, setSel] = useState(["CONFIRMADO"]);
  const [copied, setCopied] = useState(false);
  const toggle = k => setSel(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);

  const monthEvs = events
    .filter(e => { const d = new Date(e.date + "T12:00:00"); return d.getFullYear() === year && d.getMonth() === month && sel.includes(e.type); })
    .sort((a, b) => a.date.localeCompare(b.date));

  function buildText() {
    if (!monthEvs.length) return MONTHS_PT[month] + " " + year + "\nNenhum show neste filtro.";
    const lines = [MONTHS_PT[month] + " " + year, ""];
    monthEvs.forEach(ev => {
      const dt = new Date(ev.date + "T12:00:00");
      const d = String(dt.getDate()).padStart(2, "0");
      const dow = DAYS_PT[dt.getDay()];
      lines.push(d + "/" + String(month + 1).padStart(2, "0") + " " + dow + " | " + ev.city + "/" + ev.state + (ev.time ? " - " + ev.time : ""));
    });
    return lines.join("\n");
  }

  function copy() {
    navigator.clipboard.writeText(buildText()).then(() => {
      setCopied(true);
      setTimeout(() => { setCopied(false); onClose(); }, 1500);
    });
  }

  return (
    <div style={{ position: "fixed", inset: 0, background: T.modalBg, zIndex: 200, display: "flex", alignItems: isMobile ? "flex-end" : "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ background: T.surface, borderRadius: isMobile ? "18px 18px 0 0" : "14px", width: "100%", maxWidth: 460, padding: "20px 18px 28px", boxShadow: `0 -4px 32px ${T.shadowMd}` }} onClick={e => e.stopPropagation()}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
          <span style={{ fontWeight: 800, fontSize: 14, color: T.text, display: "flex", alignItems: "center", gap: 8 }}><I n="Filter" size={14} /> Selecionar tipos</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: T.textSub, display: "flex" }}><I n="X" size={15} /></button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7, marginBottom: 16 }}>
          {EVENT_TYPES.map(t => {
            const on = sel.includes(t.key);
            const { bg, text } = typeColors(t.key, dark);
            return (
              <button key={t.key} onClick={() => toggle(t.key)} style={{ display: "flex", alignItems: "center", gap: 7, padding: "8px 10px", borderRadius: 8, border: `2px solid ${on ? bg : T.border}`, background: on ? bg : T.surfaceAlt, cursor: "pointer" }}>
                <span style={{ width: 14, height: 14, borderRadius: 3, border: `2px solid ${on ? text : T.inputBorder}`, background: on ? text : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {on && <I n="Check" size={9} style={{ color: on ? bg : "transparent" }} />}
                </span>
                <span style={{ fontSize: 11, fontWeight: 700, color: on ? text : T.textSub }}>{t.label}</span>
              </button>
            );
          })}
        </div>
        <div style={{ background: T.surfaceAlt, borderRadius: 8, padding: "10px 12px", marginBottom: 12, maxHeight: 120, overflowY: "auto", border: `1px solid ${T.border}` }}>
          {!monthEvs.length
            ? <span style={{ fontSize: 12, color: T.textMuted }}>Nenhum show com este filtro.</span>
            : monthEvs.map(ev => {
              const dt = new Date(ev.date + "T12:00:00");
              const d = String(dt.getDate()).padStart(2, "0");
              const dow = DAYS_PT[dt.getDay()];
              return <div key={ev.id} style={{ fontSize: 12, color: T.text, padding: "2px 0", fontFamily: "monospace" }}>{d}/{String(month + 1).padStart(2, "0")} {dow} | {eventTitle(ev)}{ev.time ? " - " + ev.time : ""}</div>;
            })
          }
        </div>
        <button onClick={copy} disabled={!monthEvs.length} style={{ width: "100%", background: copied ? T.accent : T.text, color: copied ? T.accentText : T.bg, border: "none", borderRadius: 10, padding: 13, fontSize: 14, fontWeight: 800, cursor: !monthEvs.length ? "not-allowed" : "pointer", opacity: !monthEvs.length ? 0.4 : 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {copied ? <><I n="Check" size={15} /> COPIADO!</> : <><I n="Copy" size={15} /> COPIAR TEXTO</>}
        </button>
      </div>
    </div>
  );
}
